import { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  House,
  BookOpen,
  Users as UsersIcon,
  UsersThree,
  Wallet,
  ChatCircleDots,
  Buildings,
  ClockCounterClockwise,
  Bell,
  SignOut,
  UserCircle,
  List,
  X,
  ClipboardText,
  Question,
  VideoCamera,
  CalendarBlank,
  BookBookmark,
  BookOpenText,
  ChalkboardTeacher,
  TrendDown,
  CaretDoubleLeft,
  CaretDoubleRight,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import api, { coverSrc } from "@/lib/api";
import FinanceAlertPopup from "@/components/FinanceAlertPopup";
import logo from "@/assets/logo.png";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";

const ROLE_LABEL = { admin: "Administrateur", professeur: "Professeur", etudiant: "Étudiant" };

function navItems(role) {
  const base = [
    { to: "/app", label: "Tableau de bord", icon: House, end: true, color: "hsl(var(--primary))" },
    { to: "/app/cours", label: "Cours", icon: BookOpen, color: "hsl(var(--campus-orange))" },
  ];
  if (role === "admin") {
    base.push({ to: "/app/utilisateurs", label: "Utilisateurs", icon: UsersIcon, color: "hsl(258, 60%, 58%)" });
    base.push({ to: "/app/groupes", label: "Groupes", icon: UsersThree, color: "hsl(258, 60%, 58%)" });
    base.push({ to: "/app/academique", label: "Gestion académique", icon: Buildings, color: "hsl(258, 60%, 58%)" });
    base.push({ to: "/app/journal", label: "Journal d'activité", icon: ClockCounterClockwise, color: "hsl(258, 60%, 58%)" });
  }
  if (role === "etudiant") base.push({ to: "/app/notes", label: "Carnet de notes", icon: ClipboardText, color: "hsl(var(--campus-green))" });
  if (role === "admin" || role === "professeur") base.push({ to: "/app/notes-etudiants", label: "Notes des étudiants", icon: ClipboardText, color: "hsl(var(--campus-green))" });
  if (role === "admin" || role === "professeur") base.push({ to: "/app/etudiants-inscrits", label: "Étudiants inscrits", icon: UsersThree, color: "hsl(var(--campus-green))" });
  if (role === "admin") base.push({ to: "/app/finance", label: "Finance & Scolarité", icon: Wallet, color: "hsl(var(--accent))" });
  if (role === "admin" || role === "professeur") base.push({ to: "/app/banque-questions", label: "Banque de questions", icon: Question, color: "hsl(var(--campus-orange))" });
  if (role === "admin" || role === "professeur") base.push({ to: "/app/decrochage", label: "Détection du décrochage", icon: TrendDown, color: "hsl(var(--accent))" });
  base.push({ to: "/app/salles", label: "Salles de cours", icon: VideoCamera, section: "Campus virtuel", color: "hsl(189, 70%, 42%)" });
  base.push({ to: "/app/calendrier", label: "Calendrier", icon: CalendarBlank, color: "hsl(189, 70%, 42%)" });
  base.push({ to: "/app/bibliotheque", label: "Bibliothèque", icon: BookBookmark, color: "hsl(var(--campus-orange))" });
  base.push({ to: "/app/glossaire", label: "Glossaire", icon: BookOpenText, color: "hsl(var(--campus-orange))" });
  base.push({ to: "/app/enseignants", label: "Bureau des enseignants", icon: ChalkboardTeacher, color: "hsl(189, 70%, 42%)" });
  base.push({ to: "/app/espaces", label: "Espaces étudiants", icon: UsersThree, color: "hsl(189, 70%, 42%)" });
  base.push({ to: "/app/messagerie", label: "Messagerie", icon: ChatCircleDots, section: "Communication", color: "hsl(189, 70%, 42%)" });
  return base;
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("sidebarCollapsed") === "1");
  const [notifs, setNotifs] = useState([]);

  const loadNotifs = () => api.get("/notifications").then((r) => setNotifs(r.data)).catch(() => {});
  useEffect(() => {
    loadNotifs();
    const t = setInterval(loadNotifs, 20000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const toggleMobile = () => setMobileOpen((o) => !o);
  const toggleDesktop = () =>
    setCollapsed((c) => {
      localStorage.setItem("sidebarCollapsed", c ? "0" : "1");
      return !c;
    });

  const unread = notifs.filter((n) => !n.read).length;
  const items = navItems(user.role);
  const initials = user.name.split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="flex min-h-screen bg-background">
      <FinanceAlertPopup />
      {/* Sidebar */}
      <aside
        className={`fixed z-40 h-screen w-72 shrink-0 overflow-hidden border-r border-border bg-card shadow-xl transition-[transform,width] duration-300 ease-in-out lg:relative lg:translate-x-0 lg:shadow-none
          ${mobileOpen ? "translate-x-0" : "-translate-x-full"}
          ${collapsed ? "lg:w-0 lg:border-r-0" : "lg:w-72"}`}
      >
        <div className="flex h-full w-72 flex-col">
        <div className="relative flex items-center gap-3 border-b border-border px-6 py-5">
          <img src={logo} alt="École de Commerce de Lyon" className="h-11 w-11 shrink-0 object-contain" />
          <div>
            <p className="font-display text-base font-extrabold leading-none tracking-tight text-foreground">ECL Abidjan</p>
            <p className="mt-1 text-xs uppercase tracking-[0.15em] text-muted-foreground">Campus virtuel</p>
          </div>
          <button onClick={toggleDesktop} className="absolute right-3 top-1/2 hidden -translate-y-1/2 rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-secondary hover:text-primary lg:block" data-testid="sidebar-collapse-btn" title="Réduire le menu" aria-label="Réduire le menu">
            <CaretDoubleLeft size={20} weight="bold" />
          </button>
          <button onClick={toggleMobile} className="absolute right-3 top-4 rounded-md p-1 text-muted-foreground hover:bg-secondary lg:hidden" data-testid="sidebar-close">
            <X size={20} />
          </button>
          <div className="flag-stripe absolute inset-x-0 bottom-0" aria-hidden="true"><span /><span /><span /></div>
        </div>
        <nav className="flex-1 space-y-1 overflow-y-auto p-4">
          {items.map((it) => (
            <div key={it.to}>
              {it.section && (
                <p className="px-4 pb-1 pt-4 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">{it.section}</p>
              )}
              <NavLink
                to={it.to}
                end={it.end}
                data-testid={`nav-${it.to.replace("/app", "").replace("/", "") || "home"}`}
                className={({ isActive }) =>
                  `group relative flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-primary text-primary-foreground shadow-sm"
                      : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {isActive && <span className="absolute left-0 top-1/2 h-6 -translate-y-1/2 rounded-r bg-accent" style={{ width: 3 }} />}
                    <it.icon size={20} weight="duotone" className="transition-transform group-hover:scale-110" style={{ color: isActive ? "currentColor" : it.color }} />
                    {it.label}
                  </>
                )}
              </NavLink>
            </div>
          ))}
        </nav>
        <div className="border-t border-border p-4">
          <div className="flex items-center gap-3 rounded-lg bg-secondary p-3">
            <div className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-primary to-accent text-sm font-bold text-primary-foreground">
              {coverSrc(user.avatar) ? <img src={coverSrc(user.avatar)} alt={user.name} className="h-full w-full object-cover" /> : initials}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold">{user.name}</p>
              <p className="text-xs text-muted-foreground">{ROLE_LABEL[user.role]}</p>
            </div>
          </div>
        </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-30 bg-black/50 backdrop-blur-sm lg:hidden"
            onClick={toggleMobile}
          />
        )}
      </AnimatePresence>

      {/* Main */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-20 flex items-center justify-between gap-3 border-b border-border bg-card/90 px-4 py-3 backdrop-blur-lg lg:px-8">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={toggleMobile} data-testid="menu-toggle" aria-label="Ouvrir le menu" className="lg:hidden">
              <List size={22} />
            </Button>
            {collapsed && (
              <Button variant="outline" size="sm" onClick={toggleDesktop} data-testid="sidebar-expand-btn" className="hidden lg:inline-flex" title="Afficher le menu">
                <CaretDoubleRight size={16} className="mr-1.5" /> Menu
              </Button>
            )}
            <Badge className="hidden bg-accent text-accent-foreground hover:bg-accent sm:inline-flex">Campus Abidjan</Badge>
          </div>
          <div className="flex items-center gap-2">
            <DropdownMenu onOpenChange={(o) => o && unread && api.post("/notifications/read-all").then(loadNotifs)}>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="relative" data-testid="notifications-btn">
                  <Bell size={22} weight="duotone" />
                  {unread > 0 && (
                    <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent px-1 text-[10px] font-bold text-accent-foreground">
                      {unread}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80">
                <DropdownMenuLabel>Notifications</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {notifs.length === 0 && <p className="px-3 py-6 text-center text-sm text-muted-foreground">Aucune notification</p>}
                {notifs.slice(0, 10).map((n) => (
                  <div key={n.id} className="px-3 py-2 text-sm">
                    <p className={n.read ? "text-muted-foreground" : "font-medium"}>{n.message}</p>
                  </div>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-primary to-accent text-sm font-bold text-primary-foreground transition-transform hover:scale-105" data-testid="user-menu">
                  {coverSrc(user.avatar) ? <img src={coverSrc(user.avatar)} alt={user.name} className="h-full w-full object-cover" /> : initials}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>{user.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/app/mon-compte")} data-testid="my-account-btn">
                  <UserCircle size={18} className="mr-2" /> Mon compte
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => {
                    logout();
                    navigate("/");
                  }}
                  data-testid="logout-btn"
                >
                  <SignOut size={18} className="mr-2" /> Déconnexion
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
            >
              <Outlet />
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
    </div>
  );
}
